import { useState, useEffect, useCallback } from 'react';
import { useAppointment } from '../context/appointmentContext';
import { useTestContext } from '../context/testContext';
import { useVideo } from '../context/videoContext';
import { Flex } from '../component-library';
import { useNavigate } from 'react-router-dom';
import { useCalibration } from '../context/calibrationContext';
import { calibrationConstants } from '../helpers/calibration-constants';
import MainPanelContent from './patient/main-panel-content';
import { useNavigationSync } from '../context/navigationContext';
import { useReceiveMessage } from '../context/messageContext';
import { ScreenSizeGuard } from './session/ScreenSizeGuard';
import { PatientWaiting } from './session/PatientWaiting';
import { PatientSessionShell } from './session/calibration/PatientSessionShell';
import { PatientCardCalibration, PatientRulerCalibration, PatientCameraCalibration, PatientDistance } from './session/calibration/PatientCalibrationScreens';
import { useRestorePatientTab } from './session/calibration/sync';

// Non-test patient screens rendered in the IBISengage SessionLayout (waiting P2/P4/P7, card, ruler,
// camera calibration, distance P5/P6). Test stimuli keep the legacy full-screen MainPanelContent.
const SESSION_TABS = [null, undefined, 'holding', 'calibration-selection', 'screen-calibration', 'card', 'ruler', 'camera-calibration', 'pupillaryDistance'];
const sessionContent = (tab) => {
  switch (tab) {
    case 'card':
      return <PatientCardCalibration />;
    case 'ruler':
      return <PatientRulerCalibration desktop />;
    case 'camera-calibration':
      return <PatientCameraCalibration />;
    case 'pupillaryDistance':
      return <PatientDistance />;
    default:
      return <PatientWaiting />;
  }
};

const PatientScreen = () => {
  let navigate = useNavigate();
  const { setMultipleTestFieldValues } = useTestContext();
  const {
    updateCalibrationData,
    calibrationData: { circleDimensionsPx },
  } = useCalibration();
  const { disconnect } = useVideo();
  const { appointment } = useAppointment();
  const receivedMessage = useReceiveMessage();
  const { activePatientTab: activeTab, setActivePatientTab: setActiveTab } = useNavigationSync();
  const [logmarData, setLogmarData] = useState({});
  const [logmarType, setLogmarType] = useState();
  const [optimaData, setOptimaData] = useState();
  const [visualFieldTestControl, setVisualFieldTestControl] = useState('stopVisualField');
  const { blindSpotThumbnailHeightPx, blindSpotThumbnailWidthPx, blindSpotThumbnailY } = calibrationConstants;
  const [blindSpotUpdateMessage, setBlindSpotUpdateMessage] = useState(null);
  useRestorePatientTab(activeTab, setActiveTab, { desktop: true });

  const [blindSpotData, setBlindSpotData] = useState({
    blindSpotVideo: {
      clientX: window.screen.availWidth / 2 - blindSpotThumbnailWidthPx / 2,
      clientY: blindSpotThumbnailY,
      circleDimensionsPx: circleDimensionsPx,
    },
    blindSpot: {
      clientX: window.screen.availWidth / 2 - circleDimensionsPx / 2,
      clientY: blindSpotThumbnailY + blindSpotThumbnailHeightPx / 2,
      circleDimensionsPx: circleDimensionsPx,
      show: false,
    },
  });

  const postMessage = useCallback(
    (messageContent) => {
      var messageObj = {
        message: {
          destination: 'optician',
          content: messageContent,
        },
      };
      appointment.websocket.postMessage(messageObj);
    },
    [appointment?.websocket],
  );

  const movementNotification = (messageContent) => {
    postMessage(messageContent);
  };

  const testNotification = (messageContent) => {
    const message = {
      type: 'visualFieldUpdate',
      content: messageContent,
    };
    postMessage(message);
  };

  useEffect(() => {
    switch (receivedMessage?.type) {
      case 'navigateToWelcomeScreen':
        return navigate(appointment?.currentAppointment?.room_name);
      case 'screen-calibration':
        setActiveTab('holding');
        break;
      case 'stepCardCalibration':
      case 'cardDimensions':
        setActiveTab('card');
        break;
      case 'stepRulerCalibration':
        setActiveTab('ruler');
        break;
      case 'rulerDimensions':
        const { rulerWidth, rulerHeight } = receivedMessage?.content;
        updateCalibrationData({ rulerDimensions: { widthMm: rulerWidth, heightMm: rulerHeight } });
        setActiveTab('ruler');
        break;
      case 'blindSpotXPosition':
        setBlindSpotUpdateMessage({ type: 'blindSpotXPosition', msgBody: receivedMessage?.content });
        break;
      case 'blindSpotVideoXPosition':
        setBlindSpotUpdateMessage({ type: 'blindSpotVideoXPosition', msgBody: receivedMessage?.content });
        break;
      case 'visualAcuityTest':
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceFromScreen,
          characters: receivedMessage?.content.logmarCharacters,
        });
        setLogmarType(receivedMessage?.content.logmarType);
        setActiveTab('logmar');
        break;
      case 'resizeLogmar':
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceReferenceInMM,
          characters: receivedMessage?.content.logmarCharacters,
        });
        break;
      case 'resizeLogmarImage':
        setLogmarData({
          size: receivedMessage?.content.contentSize,
          distance: receivedMessage?.content.distanceReferenceInMM,
          logmarContent: receivedMessage?.content.logmarContent,
        });
        break;
      case 'optimaTextContents':
        setOptimaData({
          textContents: receivedMessage?.content.textContents.fontSizes,
        });
        break;
      case 'randomE':
        setActiveTab('randomE');
        break;
      case 'kaysOptotypes':
        setActiveTab('kaysOptotypes');
        break;
      case 'kaysMultiple':
        setActiveTab('kaysMultiple');
        break;
      case 'optimaChart':
        setActiveTab('optimaChart');
        break;
      case 'peleRobsonTest':
        setActiveTab('peleRobson');
        break;
      case 'visualField':
        setActiveTab('visualField');
        break;
      case 'visualFieldTwo':
        setActiveTab('visualFieldTwo');
        break;
      case 'ishiharaPlate':
        setActiveTab('ishihara');
        break;
      case 'heartBeat':
        postMessage({ type: 'heartBeatResponse', room_name: receivedMessage?.room_name });
        break;
      case 'opticianLoggedOut':
        disconnect();
        navigate(`${process.env.PUBLIC_URL}/error`, {
          state: { error: 'This session has been closed. Close your browser to end the appointment.', title: 'Session closed' },
        });
        break;
      case 'contrastSensitivity':
        setActiveTab('contrastSensitivity');
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceFromScreen,
          backgroundColor: receivedMessage?.content.backgroundColor,
          fontColor: receivedMessage?.content.fontColor,
          characters: receivedMessage?.content.characters,
        });
        break;
      case 'updateContrastSensitivityContent':
        let currentLogmarData = logmarData;
        currentLogmarData['backgroundColor'] = receivedMessage?.content.backgroundColor;
        currentLogmarData['fontColor'] = receivedMessage?.content.fontColor;
        currentLogmarData['characters'] = receivedMessage?.content.characters;
        setMultipleTestFieldValues(currentLogmarData);
        setLogmarData(currentLogmarData);
        break;
      case 'startVisualField':
        setVisualFieldTestControl({
          command: 'startVisualField',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'stopVisualField':
        setVisualFieldTestControl({
          command: 'stopVisualField',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'initColors':
        setVisualFieldTestControl({
          command: 'initColors',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'reloadTest':
        setVisualFieldTestControl({
          command: 'reloadTest',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'visualFieldForthValley':
        return setActiveTab('visualFieldForthValley');
      case 'startRefraction':
        return setActiveTab('refraction');
      case 'startRefractionTwo':
        return setActiveTab('refractionTwo');
      case 'camera-calibration':
      case 'initialiseToolDetections':
      case 'perceivedFocalLength':
        setActiveTab('camera-calibration');
        break;
      case 'blindspot-camera-calibration':
      case 'blindspotPerceivedFocalLength':
        setActiveTab('blindspot-camera-calibration');
        break;
      default:
        break;
    }
  }, [receivedMessage]);

  if (SESSION_TABS.includes(activeTab)) {
    return <PatientSessionShell activeTab={activeTab}>{sessionContent(activeTab)}</PatientSessionShell>;
  }

  return (
    <ScreenSizeGuard>
    <Flex width='100%'>
      <MainPanelContent
        postMessage={postMessage}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        blindSpotData={blindSpotData}
        setBlindSpotData={setBlindSpotData}
        movementNotification={movementNotification}
        logmarData={logmarData}
        logmarType={logmarType}
        optimaData={optimaData}
        visualFieldTestControl={visualFieldTestControl}
        testNotification={testNotification}
        blindSpotUpdateMessage={blindSpotUpdateMessage}
        setBlindSpotUpdateMessage={setBlindSpotUpdateMessage}
      />
    </Flex>
    </ScreenSizeGuard>
  );
};

export default PatientScreen;
